import React, { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";

const PIP_STYLES = `
  body {
    margin: 0;
    height: 100vh;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background: rgb(11, 9, 9);
    font-family: "Montserrat", sans-serif;
    color: #f5e8c7d1;
    user-select: none;
    overflow: hidden;
  }
  .pip-label {
    font-size: 13px;
    font-weight: bold;
    letter-spacing: 2px;
    text-transform: uppercase;
    display: flex;
    align-items: center;
    gap: 7px;
    opacity: 0.85;
  }
  .pip-dot {
    width: 9px;
    height: 9px;
    border-radius: 50%;
  }
  .pip-clock {
    font-family: "Righteous", cursive;
    font-size: 64px;
    line-height: 1.1;
    color: #f5e8c7d1;
  }
  .pip-status {
    font-size: 12px;
    opacity: 0.6;
    margin-bottom: 8px;
  }
  .pip-btn {
    background: transparent;
    border: 1px solid #f5e8c766;
    color: #f5e8c7d1;
    border-radius: 9999px;
    padding: 4px 18px;
    font-size: 13px;
    font-family: "Montserrat", sans-serif;
    cursor: pointer;
    transition: background 0.2s ease;
  }
  .pip-btn:hover {
    background: #f5e8c71a;
  }
`;

const FONT_LINK =
  "https://fonts.googleapis.com/css2?family=Righteous&family=Montserrat:wght@700&display=swap";

export function isPiPSupported() {
  return "documentPictureInPicture" in window;
}

function TimerPiP({
  pipOpen,
  onPipClose,
  minutesText,
  secondsText,
  isRunning,
  started,
  Atime,
  onPause,
  onResume,
}) {
  const [pipWindow, setPipWindow] = useState(null);
  const pipWindowRef = useRef(null);
  pipWindowRef.current = pipWindow;

  const openPiP = useCallback(async () => {
    if (!isPiPSupported() || pipWindowRef.current) return;
    try {
      const win = await window.documentPictureInPicture.requestWindow({
        width: 300,
        height: 200,
      });

      const fontLink = win.document.createElement("link");
      fontLink.rel = "stylesheet";
      fontLink.href = FONT_LINK;
      win.document.head.appendChild(fontLink);

      const style = win.document.createElement("style");
      style.textContent = PIP_STYLES;
      win.document.head.appendChild(style);

      win.addEventListener("pagehide", () => {
        setPipWindow(null);
        onPipClose?.();
      });
      setPipWindow(win);
    } catch (err) {
      onPipClose?.();
      console.error("PiP failed:", err);
    }
  }, [onPipClose]);

  const closePiP = useCallback(() => {
    pipWindowRef.current?.close();
    setPipWindow(null);
  }, []);

  // Respond to external open/close control
  useEffect(() => {
    if (pipOpen) {
      openPiP();
    } else {
      closePiP();
    }
  }, [pipOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Let Chrome auto-open the PiP window when the user switches tabs
  useEffect(() => {
    if (!isPiPSupported() || !("mediaSession" in navigator)) return;
    try {
      navigator.mediaSession.setActionHandler("enterpictureinpicture", () => {
        openPiP();
      });
    } catch (err) {
      return;
    }
    return () => {
      try {
        navigator.mediaSession.setActionHandler("enterpictureinpicture", null);
      } catch (err) {}
    };
  }, [openPiP]);

  useEffect(() => closePiP, [closePiP]);

  if (!pipWindow) return null;

  return createPortal(
    <>
      <div className="pip-label">
        <span
          className="pip-dot"
          style={{ backgroundColor: Atime ? "red" : "green" }}
        ></span>
        {Atime ? "Break" : "Focus"}
      </div>
      <div className="pip-clock">
        {minutesText}:{secondsText}
      </div>
      <div className="pip-status">{isRunning ? "Running" : "Paused"}</div>
      {started && (
        <button className="pip-btn" onClick={isRunning ? onPause : onResume}>
          {isRunning ? "Pause" : "Resume"}
        </button>
      )}
    </>,
    pipWindow.document.body
  );
}

export default TimerPiP;
